import type { ResolvedQueryLeft } from '@/features/expressions/query'
import { createApi } from '@reduxjs/toolkit/query/react'
import { useAccountStore } from '@/stores/account-store'
import { createAuthBaseQuery } from './base-query'
import { API } from '@/config/constants'
import {
  UserSchema,
  PlanSchema,
  UsageSchema,
  QuotaCheckSchema,
  EntitlementsResponseSchema,
} from '@/types/schemas/api'
import type { User, Plan, Usage, QuotaCheck, EntitlementsResponse } from '@/types/schemas/api'
import type {
  ProfileCollection,
  SaveProfileCollectionResponse,
} from '@/features/sliced-form/schemas/profile'

export interface EmailChangeAck {
  status: string
  warning?: string | null
}

/** Extract `.data` from the response envelope `{ success, data, meta }`. */
function unwrapEnvelope<T = unknown>(raw: unknown): T {
  if (raw != null && typeof raw === 'object' && 'success' in raw && 'data' in raw) {
    return (raw as { data: T }).data
  }
  return raw as T
}

/**
 * RTK Query API for the Edge Users service.
 * Handles: user profile, billing, subscriptions, compliance forms, chart layouts, study templates, exports, tokens.
 * Auth: accessToken
 */
export const edgeUsersApi = createApi({
  reducerPath: 'edgeUsersApi',
  baseQuery: createAuthBaseQuery(API.users, 'access'),
  tagTypes: ['User', 'Plans', 'Tokens', 'Usage', 'Entitlements', 'Exports', 'Profiles'],
  endpoints: (builder) => ({
    // ─── User Management ───

    getUser: builder.query<User, void>({
      query: () => '/v2/user',
      transformResponse: (raw) => UserSchema.parse(unwrapEnvelope(raw)),
      providesTags: ['User'],
    }),

    updateUser: builder.mutation<User, Partial<User>>({
      query: (body) => ({
        url: '/v2/user',
        method: 'PATCH',
        body,
      }),
      transformResponse: (raw) => UserSchema.parse(unwrapEnvelope(raw)),
      invalidatesTags: ['User'],
    }),

    // ─── Billing: Checkout & Portal ───

    getSubscriptionPlans: builder.query<Plan[], void>({
      query: () => '/v2/billing/plans',
      transformResponse: (raw) => PlanSchema.array().parse(unwrapEnvelope(raw)),
      providesTags: ['Plans'],
    }),

    createCheckoutSession: builder.mutation<
      { url: string },
      {
        plan: string
        interval: string
        promotionCode?: string
        cobra?: boolean
        // Rewardful. `referral` decides who earns the commission; `affiliateCoupon` is the
        // coupon Rewardful assigned this visitor, honoured only if `campaign` is configured
        // to allow it (a campaign has one coupon per affiliate).
        referral?: string
        campaign?: string
        affiliateCoupon?: string
      }
    >({
      query: (body) => ({
        url: '/v2/billing/create-checkout-session',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => unwrapEnvelope<{ url: string }>(raw),
      invalidatesTags: ['User'],
    }),

    // Cobra Premier eligibility for the current user (allowlist check, server-side).
    // Drives the soft-offer + the cobra activation flow.
    getCobraEligibility: builder.query<{ eligible: boolean }, void>({
      query: () => '/v2/billing/cobra-eligibility',
      transformResponse: (raw) => unwrapEnvelope<{ eligible: boolean }>(raw),
    }),

    // returnPath: the app path Stripe returns the customer to after the portal
    // (e.g. the current page). The backend supplies the stage origin and guards it.
    createPortalSession: builder.mutation<{ url: string }, { returnPath?: string } | void>({
      query: (arg) => ({
        url: '/v2/billing/create-portal-session',
        method: 'POST',
        body: { returnPath: arg?.returnPath },
      }),
      transformResponse: (raw) => unwrapEnvelope<{ url: string }>(raw),
    }),

    // Apply a professional-status change to the active subscription (add/remove
    // the market-data add-on, prorated). Status is decided server-side from the
    // user's latest survey — this takes no body.
    applyProfessionalSwitch: builder.mutation<{ status: string; professional: boolean }, void>({
      query: () => ({
        url: '/v2/billing/apply-professional-switch',
        method: 'POST',
      }),
      transformResponse: (raw) => unwrapEnvelope<{ status: string; professional: boolean }>(raw),
      invalidatesTags: ['User'],
    }),

    // ─── Billing: Plan Changes ───
    // NOTE: plan changes are migrating to the Stripe Customer Portal. This stays
    // until the single-vs-multi-product catalog decision is settled (a multi-product
    // catalog still needs backend-driven cross-tier downgrades). See billing research §11a.

    updateSubscription: builder.mutation<
      unknown,
      { subscriptionId: string; plan: string; interval: string }
    >({
      query: (body) => ({
        url: '/v2/billing/update-subscription',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),

    getPaymentHistory: builder.query<unknown[], void>({
      query: () => '/v2/billing/payment-history',
      transformResponse: (raw) => unwrapEnvelope<unknown[]>(raw),
    }),

    attributeReferral: builder.mutation<unknown, { referralCode: string }>({
      query: (body) => ({
        url: '/v2/billing/attribute-referral',
        method: 'POST',
        body,
      }),
    }),

    // ─── Tokens ───

    getTokenBalance: builder.query<{ balance: number; lifetime_purchased: number }, void>({
      query: () => '/v2/tokens/balance',
      transformResponse: (raw) =>
        unwrapEnvelope<{ balance: number; lifetime_purchased: number }>(raw),
      providesTags: ['Tokens'],
    }),

    purchaseTokens: builder.mutation<{ url: string }, { packId: string }>({
      query: (body) => ({
        url: '/v2/tokens/purchase',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => unwrapEnvelope<{ url: string }>(raw),
      invalidatesTags: ['Tokens'],
    }),

    // ─── Usage ───

    getUsage: builder.query<Usage, void>({
      query: () => '/v2/usage',
      transformResponse: (raw) => UsageSchema.parse(unwrapEnvelope(raw)),
      providesTags: ['Usage'],
    }),

    // ─── Entitlements ───
    //
    // Every plan limit and feature flag for the signed-in user, owned by the
    // server (`services/entitlements.py`). `PlanConstraintProvider` is the one
    // consumer; components read the limits through its hooks, never from here.
    // Invalidated by 'User' so a plan change re-fetches with the subscription.
    getEntitlements: builder.query<EntitlementsResponse, void>({
      query: () => '/v2/users/me/entitlements',
      transformResponse: (raw) => EntitlementsResponseSchema.parse(unwrapEnvelope(raw)),
      // Also tagged 'User' so an in-app plan change (updateSubscription,
      // applyProfessionalSwitch — both invalidate 'User') re-fetches the limits
      // in the same pass that re-fetches the subscription the tier derives from.
      // Without it the tier would update while the payload stayed stale.
      providesTags: ['User', 'Entitlements'],
    }),

    checkQuota: builder.query<QuotaCheck, string>({
      query: (resource) => `/v2/usage/check/${resource}`,
      transformResponse: (raw) => QuotaCheckSchema.parse(unwrapEnvelope(raw)),
      providesTags: ['Usage'],
    }),

    // ─── Compliance ───

    getLastProfessionalResponse: builder.query<unknown, void>({
      query: () => '/v2/user/nonprofessional-survey/latest',
      transformResponse: (raw) => unwrapEnvelope(raw),
    }),

    submitBillingComplianceForm: builder.mutation<unknown, Record<string, unknown>>({
      query: (body) => ({
        url: '/v2/user/nonprofessional-survey/billing',
        method: 'POST',
        body,
      }),
    }),

    submitSubscriptionComplianceForm: builder.mutation<unknown, Record<string, unknown>>({
      query: (body) => ({
        url: '/v2/user/nonprofessional-survey/subscription',
        method: 'POST',
        body,
      }),
    }),

    getComplianceNextVersion: builder.query<{ version: number; expired: boolean }, void>({
      query: () => '/v2/user/nonprofessional-survey/next-version',
      transformResponse: (raw) => unwrapEnvelope(raw) as { version: number; expired: boolean },
    }),

    // ─── Profiles ───

    getUserProfiles: builder.query<unknown, { page: string; type: string }>({
      query: ({ page, type }) =>
        `/v2/user/profiles/${encodeURIComponent(page)}/${encodeURIComponent(type)}`,
      transformResponse: (raw) => unwrapEnvelope(raw),
      providesTags: (_result, _error, { page, type }) => [
        { type: 'Profiles', id: `${page}-${type}` },
      ],
    }),

    saveUserProfile: builder.mutation<
      SaveUserProfileResponse,
      { page: string; type: string; profile: unknown }
    >({
      query: ({ page, type, profile }) => ({
        url: `/v2/user/profiles/${encodeURIComponent(page)}/${encodeURIComponent(type)}`,
        method: 'POST',
        body: profile,
      }),
      transformResponse: (raw) => unwrapEnvelope(raw) as SaveUserProfileResponse,
    }),

    // ─── Profile Collection (see plan §4) ───
    //
    // A ProfileCollection is profiles + tree together — one wire shape,
    // one snapshot. Two DDB records under the hood.

    getProfileCollection: builder.query<ProfileCollection, { page: string; type: string }>({
      query: ({ page, type }) =>
        `/v2/user/profileCollections/${encodeURIComponent(page)}/${encodeURIComponent(type)}`,
      transformResponse: (raw) => unwrapEnvelope<ProfileCollection>(raw),
      providesTags: (_result, _error, { page, type }) => [
        { type: 'Profiles', id: `${page}-${type}-collection` },
      ],
    }),

    saveProfileCollection: builder.mutation<
      SaveProfileCollectionResponse,
      { page: string; type: string; collection: ProfileCollection }
    >({
      query: ({ page, type, collection }) => ({
        url: `/v2/user/profileCollections/${encodeURIComponent(page)}/${encodeURIComponent(type)}`,
        method: 'POST',
        body: collection,
      }),
      transformResponse: (raw) => unwrapEnvelope<SaveProfileCollectionResponse>(raw),
      invalidatesTags: (_result, _error, { page, type }) => [
        { type: 'Profiles', id: `${page}-${type}-collection` },
      ],
    }),

    // ─── User Settings (singletons + expressions) ───
    //
    // Settings live under the ``setting#{page}#{type}`` SK family on the
    // backend. They're singleton-ish blobs (favorites, expression
    // libraries) that used to squat on the profile API. Same wire
    // envelope as the profile endpoints — a plain list of dicts.

    getUserSettings: builder.query<unknown, { page: string; type: string }>({
      query: ({ page, type }) =>
        `/v2/user/settings/${encodeURIComponent(page)}/${encodeURIComponent(type)}`,
      transformResponse: (raw) => unwrapEnvelope(raw),
      providesTags: (_result, _error, { page, type }) => [
        { type: 'Profiles', id: `setting-${page}-${type}` },
      ],
    }),

    saveUserSettings: builder.mutation<
      SaveUserSettingsResponse,
      { page: string; type: string; setting: unknown }
    >({
      query: ({ page, type, setting }) => ({
        url: `/v2/user/settings/${encodeURIComponent(page)}/${encodeURIComponent(type)}`,
        method: 'POST',
        body: setting,
      }),
      transformResponse: (raw) => unwrapEnvelope(raw) as SaveUserSettingsResponse,
      invalidatesTags: (_result, _error, { page, type }) => [
        { type: 'Profiles', id: `setting-${page}-${type}` },
      ],
    }),

    // ─── Exports (moved from data API) ───

    createExport: builder.mutation<ExportRunResult, { profileId: string; body: ExportRequestBody }>(
      {
        // The id token is added HERE (not in the caller's `body`) so it never
        // enters the mutation's stored `originalArgs` (Redux DevTools) — it only
        // exists on the outgoing request. The server forwards it to the financial
        // API, which validates access tokens; it rides in the body, not a header,
        // because the shared auth-fetch layer drops custom headers.
        query: ({ profileId, body }) => ({
          url: `/v2/exports/${encodeURIComponent(profileId)}`,
          method: 'POST',
          body: {
            ...body,
            data_auth_token: useAccountStore.getState().session?.tokens.accessToken ?? null,
          },
        }),
        transformResponse: (raw) => unwrapEnvelope<ExportRunResult>(raw),
        invalidatesTags: ['Exports', 'Usage'],
      },
    ),

    listExports: builder.query<ExportItemResponse[], string>({
      query: (profileId) => `/v2/exports/${encodeURIComponent(profileId)}`,
      transformResponse: (raw) => unwrapEnvelope<ExportItemResponse[]>(raw),
      providesTags: ['Exports'],
    }),

    getExportDownloadUrl: builder.query<{ url: string }, { profileId: string; exportId: string }>({
      query: ({ profileId, exportId }) =>
        `/v2/exports/${encodeURIComponent(profileId)}/${encodeURIComponent(exportId)}`,
      transformResponse: (raw) => unwrapEnvelope<{ url: string }>(raw),
    }),

    deleteExport: builder.mutation<
      { status: string; export_id: string },
      { profileId: string; exportId: string }
    >({
      query: ({ profileId, exportId }) => ({
        url: `/v2/exports/${encodeURIComponent(profileId)}/${encodeURIComponent(exportId)}`,
        method: 'DELETE',
      }),
      transformResponse: (raw) => unwrapEnvelope<{ status: string; export_id: string }>(raw),
      invalidatesTags: ['Exports'],
    }),

    // ─── Misc ───

    sendFeedbackEmail: builder.mutation<unknown, { email: string; message: string }>({
      query: (body) => ({
        url: '/v2/mail/send',
        method: 'POST',
        body,
      }),
    }),

    verifyAdminCode: builder.mutation<unknown, { code: string }>({
      query: (body) => ({
        url: '/v2/user/code/verify',
        method: 'POST',
        body,
      }),
    }),

    // ─── Email Change ───
    // `warning` is a non-fatal caveat (currently: local dev writes the Cognito pool
    // and the Dynamo mirror to different places). Surfaced as a toast, never thrown.

    initiateEmailChange: builder.mutation<EmailChangeAck, { newEmail: string }>({
      query: (body) => ({
        url: '/v2/change-email',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => unwrapEnvelope<EmailChangeAck>(raw),
    }),

    confirmEmailChange: builder.mutation<EmailChangeAck, { code: string }>({
      query: (body) => ({
        url: '/v2/change-email-confirm',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => unwrapEnvelope<EmailChangeAck>(raw),
      invalidatesTags: ['User'],
    }),

    resendEmailChangeCode: builder.mutation<unknown, void>({
      query: () => ({
        url: '/v2/change-email/resend',
        method: 'POST',
      }),
    }),
  }),
})

// ─── Profile save response ───

/** Envelope returned by `POST /v2/user/profiles/{page}/{type}`. */
export interface SaveUserProfileResponse {
  status?: string
  updatedAt?: string
  schemaVersion?: number
}

/** Envelope returned by ``POST /v2/user/settings/{page}/{type}``. Same shape as the profiles one. */
export type SaveUserSettingsResponse = SaveUserProfileResponse

// ─── Export Types ───

export interface ExportItemResponse {
  export_id: string
  profile_id?: string
  status: string
  created_at?: string
  row_count?: number
  column_preview?: string[]
  filter_preview?: string[]
}

/**
 * Request body for `POST /v2/exports/{profileId}`. This is the exact shape the
 * financial API's `/v2/history/columns` expects (columns as `{column}`, order as
 * `QueryOrder`), so typing it keeps a shape drift from silently reaching the
 * backend as a 400/502. Built by `profileToExportRequest`. The `data_auth_token`
 * id token is NOT part of this shape: the `createExport` mutation adds it to the
 * outgoing request itself, keeping it out of the stored mutation args.
 */
export interface ExportRequestBody {
  /** A bare `{column}` or an inlined library expression (`toWireColumns`). */
  columns: ResolvedQueryLeft[]
  filters: Record<string, unknown>
  order: { order_by: ResolvedQueryLeft; order: string }[]
  adjusted: boolean
  columnLabels: Record<string, string>
  columnPreview: string[]
  filterPreview: string[]
  offset: number
}

/**
 * Response from `POST /v2/exports/{profileId}`. `total_available`/`truncated`/
 * `next_offset` support paging through a result larger than one export can
 * return in a single call — when `truncated` is true, `next_offset` is the
 * offset to pass on the follow-up `createExport` call to fetch the next page.
 */
export interface ExportRunResult {
  /** Expression columns the backend left out (they read share-structure data). */
  stripped_columns?: string[]
  export_id: string
  status: string
  row_count: number
  total_available?: number
  truncated?: boolean
  next_offset?: number
  download_url: string
}

export const {
  useGetUserQuery,
  useUpdateUserMutation,
  useGetSubscriptionPlansQuery,
  useCreateCheckoutSessionMutation,
  useGetCobraEligibilityQuery,
  useCreatePortalSessionMutation,
  useApplyProfessionalSwitchMutation,
  useUpdateSubscriptionMutation,
  useGetPaymentHistoryQuery,
  useAttributeReferralMutation,
  useGetTokenBalanceQuery,
  usePurchaseTokensMutation,
  useGetUsageQuery,
  useGetEntitlementsQuery,
  useCheckQuotaQuery,
  useGetLastProfessionalResponseQuery,
  useSubmitBillingComplianceFormMutation,
  useSubmitSubscriptionComplianceFormMutation,
  useGetComplianceNextVersionQuery,
  useGetUserProfilesQuery,
  useSaveUserProfileMutation,
  useGetUserSettingsQuery,
  useSaveUserSettingsMutation,
  useGetProfileCollectionQuery,
  useSaveProfileCollectionMutation,
  useCreateExportMutation,
  useListExportsQuery,
  useGetExportDownloadUrlQuery,
  useLazyGetExportDownloadUrlQuery,
  useDeleteExportMutation,
  useSendFeedbackEmailMutation,
  useVerifyAdminCodeMutation,
  useInitiateEmailChangeMutation,
  useConfirmEmailChangeMutation,
  useResendEmailChangeCodeMutation,
} = edgeUsersApi
